import { useId } from "react"

import { Alert, Form, Input, Select } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import {
  AGENTCORE_REGIONS,
  isRoleTag
} from "@/pages/DeploymentsPage/application/useAgentPlatform.selector"
import {
  CloudConnectionFields,
  CloudConnectionToggle
} from "@/pages/DeploymentsPage/components/molecules/CloudConnectionStatus"

import { AgentCoreTagCommands } from "./AgentCoreTagCommands"
import { getFieldStatus } from "./helpers"

import styles from "./AgentCoreCloudConnection.module.scss"

import type { AgentCoreSettingsFieldsProps } from "."

const REGION_OPTIONS = AGENTCORE_REGIONS.map((region) => ({
  label: region,
  value: region
}))

export const AgentCoreSettingsFields = ({
  value,
  onChange
}: AgentCoreSettingsFieldsProps) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "deployments.agentCore.cloud.fields"
  })
  const { t: tConnection } = useTranslation("translation", {
    keyPrefix: "deployments.cloudConnection.fields"
  })
  const id = useId()
  const set = (patch: Partial<typeof value>) => onChange({ ...value, ...patch })
  const roleTag = value.controlledRoleTag || ""
  const invalidRoleTag = !!roleTag && !isRoleTag(roleTag)

  return (
    <CloudConnectionFields>
      <Form.Item
        label={t("regions.label")}
        help={t("regions.help")}
        htmlFor={`${id}-regions`}
      >
        <Select
          id={`${id}-regions`}
          mode="multiple"
          options={REGION_OPTIONS}
          value={value.regions}
          status={value.regions.length ? undefined : "error"}
          onChange={(regions) => set({ regions })}
        />
      </Form.Item>
      <CloudConnectionToggle
        checked={value.eventDiscovery}
        onChange={(eventDiscovery) => set({ eventDiscovery })}
        label={t("eventDiscovery.label")}
      />
      <CloudConnectionToggle
        checked={value.controlEnabled}
        onChange={(controlEnabled) => set({ controlEnabled })}
        label={tConnection("control.label")}
        help={tConnection("control.help")}
      />
      {value.controlEnabled && (
        <div className={styles.nested}>
          <Alert type="info" showIcon title={t("roleTag.guidance")} />
          <Form.Item
            label={t("roleTag.label")}
            help={invalidRoleTag ? t("roleTag.error") : t("roleTag.help")}
            validateStatus={getFieldStatus(!!roleTag, isRoleTag(roleTag))}
            hasFeedback
            htmlFor={`${id}-role-tag`}
          >
            <Input
              id={`${id}-role-tag`}
              value={roleTag}
              placeholder={t("roleTag.placeholder")}
              onChange={(e) => set({ controlledRoleTag: e.target.value })}
            />
          </Form.Item>
          <AgentCoreTagCommands controlTag={roleTag} />
        </div>
      )}
    </CloudConnectionFields>
  )
}
